import { getAddress, keccak256, toHex, type Address } from 'viem';
import { z } from 'zod';
import { SELECTED_ACCOUNT_CHAIN_ID } from '@hoodmorning/contracts';
import { readExternalExecutorSetupObservationProfile,
  readSelectedExternalExecutorSetupObservationProfile,
  type ExternalExecutorSetupObservationProfile } from './externalExecutorSetupObservation.js';

const userId = z.string().min(1).max(128).refine(value => value.trim().length > 0);
const step = z.enum(['install-execution-hooks', 'install-runtime-validation', 'arm-initial-setup']);

export type ExternalExecutorSetupStep = z.output<typeof step>;

/**
 * Stable owner-operation identity for one external executor setup step.
 *
 * The current controlling owner and observation source are intentionally not
 * part of this identity, so owner rotation and a refreshed canonical source do
 * not create a replacement operation. Release/runtime pins, the selected peer
 * and the exact step remain identity-bearing.
 */
function setupIdentity<C extends 4663 | 46630>(readProfile: (input: ExternalExecutorSetupObservationProfile<C>) => ExternalExecutorSetupObservationProfile<C>) {
return function externalExecutorSetupOperationId(input: {
  userId: string;
  profile: ExternalExecutorSetupObservationProfile<C>;
  step: ExternalExecutorSetupStep;
  peerEngine?: Address;
}) {
  const selectedUser = userId.parse(input.userId);
  const profile = readProfile(input.profile);
  const selectedStep = step.parse(input.step);
  const peer = input.peerEngine === undefined ? null : getAddress(input.peerEngine).toLowerCase();
  if (peer !== null && /^0x0{40}$/.test(peer)) throw new Error('External setup peer must be nonzero');
  if (peer !== null && [profile.vault, profile.engine.account, profile.reader.account,
    profile.callBuilder.account].includes(peer as Address)) throw new Error('External setup peer collides with a selected role');
  const identity = [profile.chainId, selectedUser, profile.moduleVersion, profile.vault,
    profile.engine, profile.keeper, profile.reader, profile.callBuilder, peer];
  return `external-setup:${keccak256(toHex(JSON.stringify(identity))).slice(2)}:${selectedStep}`;
}
}
export const externalExecutorSetupOperationId = setupIdentity<4663>(readExternalExecutorSetupObservationProfile);
export const selectedExternalExecutorSetupOperationId = setupIdentity<typeof SELECTED_ACCOUNT_CHAIN_ID>(
  readSelectedExternalExecutorSetupObservationProfile);
